import * as Yup from 'yup';

import { maxText } from 'utils/validation';
import { getUploadedFile } from 'utils/helpers';
import { formatDate, formatCalendarDate } from 'utils/date';
import {
  IdObject,
  AccountingEvent,
  AccountingExpense,
  AccountingExpenseRentalDetail,
} from 'types';

import { FormValues } from './types';

export const eventInitialValues = {
  amount: undefined,
  eventId: undefined,
  percent: undefined,
};

// Owner ruling (H7, 2026-08-19), re-verified read-only on Production
// 2026-10-13: ExpenseCategories id 16 = "Car Rental", company 3. Categories
// are freeform per-company names with no type flag, so the id is the gate --
// a name match would be a guess about intent.
export const CAR_RENTAL_CATEGORY_ID = 16;

export const isCarRentalSelected = (categories: (number | string)[] = []) =>
  categories.some((id) => Number(id) === CAR_RENTAL_CATEGORY_ID);

// Mirrors enum_ExpenseRentalDetails_rentalCompany. The server upper-cases but
// refuses anything outside these four, so this is a select, not free text.
export const RENTAL_COMPANY_OPTIONS = [
  { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'AVIS', label: 'Avis' },
  { value: 'BUDGET', label: 'Budget' },
  { value: 'OTHER', label: 'Other' },
];

// Column widths of ExpenseRentalDetails (renterName varchar(160),
// reservationNumber/agreementNumber varchar(120)).
const RENTER_NAME_MAX = 160;
const RENTAL_NUMBER_MAX = 120;

export const rentalDetailInitialValues = (
  data?: AccountingExpense['rentalDetail']
) => ({
  notes: data?.notes || '',
  renterName: data?.renterName || '',
  rentalCompany: data?.rentalCompany || '',
  agreementNumber: data?.agreementNumber || '',
  reservationNumber: data?.reservationNumber || '',
  // Booking date and agreement number are no longer entered (Owner, 13 Oct
  // 2026); an older rental's saved values are kept and sent back on edit.
  bookingDate: formatCalendarDate(data?.bookingDate),
});

// Owner, 18 Oct 2026: a car rental has ONE money input. The number typed is
// the estimate and is saved twice: as the expense amount (what reports, P&L
// and cashflow read) and as rentalDetail.estimatedAmountCents (the accrual
// estimate the QuickBooks rental lane reads). The statement step later
// PATCHes only the amount, so the two then differ and the rental is "final".
//   - estimate:    new, or still equal to its estimate -> "Estimated amount";
//                  saving keeps the two equal.
//   - final:       the amount moved away from the saved estimate -> "Final
//                  amount" with the estimate shown read-only; saving never
//                  touches the estimate.
//   - unestimated: a rental saved with no estimate (older rows) -> plain
//                  "Amount"; saving leaves the estimate empty.
export type CarRentalAmountState = 'final' | 'estimate' | 'unestimated';

export const CAR_RENTAL_AMOUNT_LABELS: Record<CarRentalAmountState, string> = {
  final: 'Final amount',
  unestimated: 'Amount',
  estimate: 'Estimated amount',
};

const dollarsToCents = (value: unknown) => {
  const text = String(value ?? '').trim();
  const number = Number(text);
  return text === '' || !Number.isFinite(number)
    ? undefined
    : Math.round(number * 100);
};

// The car rental's own part of the expense: the whole amount, or the Car
// Rental row of a category split.
const savedRentalCents = (data: AccountingExpense) =>
  data.categories?.length > 1
    ? dollarsToCents(
        data.categories.find(
          (category) => Number(category.id) === CAR_RENTAL_CATEGORY_ID
        )?.ExpenseCategoryLink?.amount
      )
    : dollarsToCents(data.amount);

const formRentalCents = (values: FormValues) =>
  values.categories.length > 1
    ? dollarsToCents(
        values.categoryAllocations?.find(
          (row) => Number(row.categoryId) === CAR_RENTAL_CATEGORY_ID
        )?.amount
      )
    : dollarsToCents(values.amount);

// Decided from the SAVED expense, so a final rental stays final while it is
// edited, and a new one stays an estimate while it is typed.
export const getCarRentalAmountState = (
  data?: AccountingExpense
): CarRentalAmountState => {
  if (!data) {
    return 'estimate';
  }
  const estimate = data.rentalDetail?.estimatedAmountCents;
  if (!data.rentalDetail) {
    // Saved as Car Rental before the rental fields existed: an older row
    // with no estimate. Re-categorised to Car Rental now: a new rental.
    return isCarRentalSelected(data.categories?.map((category) => category.id))
      ? 'unestimated'
      : 'estimate';
  }
  if (estimate == null) {
    return 'unestimated';
  }
  const saved = savedRentalCents(data);
  return saved !== undefined && saved !== Number(estimate)
    ? 'final'
    : 'estimate';
};

// A saved money amount as the input shows it: always two decimals ("850.10",
// "1050.00"), whatever the category; blank when there is none. The API sends
// FLOATs (850.1), so a bare toString() dropped the trailing zero.
export const moneyInputValue = (value?: number | string | null) =>
  value === null ||
  value === undefined ||
  value === '' ||
  !Number.isFinite(Number(value))
    ? ''
    : Number(value).toFixed(2);

const getIdsArray = <T extends IdObject>(array?: T[]) =>
  array?.map((item) => item.id) || [];

const getFormattedDate = (date?: string) => formatCalendarDate(date);

const getEvents = (events?: AccountingEvent[], initialEventId?: number) =>
  events?.map((event) => ({
    amount: event.amount,
    eventId: event.eventId,
    percent: event.percent,
  })) || [{ ...eventInitialValues, percent: 100, eventId: initialEventId }];

export const getInitialValues = (
  data?: AccountingExpense,
  initialEventId?: number,
  employeeId?: number
): FormValues => ({
  receiptUrl: data?.receiptUrl || '',
  description: data?.description || '',
  amount: moneyInputValue(data?.amount),
  categories: getIdsArray(data?.categories),
  paidDate: getFormattedDate(data?.paidDate),
  receipt: getUploadedFile(data?.receiptUrl),
  events: getEvents(data?.events, initialEventId),
  paymentDate: getFormattedDate(data?.paymentDate),
  paymentMethodId: data?.paymentMethodId?.toString() || '',
  rentalDetail: rentalDetailInitialValues(data?.rentalDetail),
  employees: employeeId ? [employeeId] : getIdsArray(data?.employees),
  categoryAllocations:
    data?.categories?.map((category) => ({
      categoryId: category.id,
      amount:
        data.categories.length === 1
          ? moneyInputValue(data.amount)
          : String(category.ExpenseCategoryLink?.amount ?? ''),
    })) || [],
});

export const validationSchema = Yup.object().shape({
  paidDate: Yup.string(),
  paymentDate: Yup.string().required(),
  paymentMethodId: Yup.string().required(),
  employees: Yup.array().required().min(1),
  categories: Yup.array().required().min(1),
  description: maxText('Description').min(5),
  receipt: Yup.object().required('File is a required field'),
  amount: Yup.number().required().typeError('Amount must be a number'),
  // Renter and rental company are NOT NULL, all-or-nothing on the server;
  // they and the reservation number are required here only when Car Rental
  // is selected.
  rentalDetail: Yup.object().when('categories', {
    is: (categories: number[]) => isCarRentalSelected(categories),
    then: (schema) =>
      schema.shape({
        // No longer entered (Owner, 13 Oct 2026); an older rental's saved
        // value is kept and sent back unchanged on edit.
        agreementNumber: Yup.string().trim().max(RENTAL_NUMBER_MAX),
        rentalCompany: Yup.string().required(
          'Rental company is a required field'
        ),
        renterName: Yup.string()
          .trim()
          .max(RENTER_NAME_MAX)
          .required('Renter name is a required field'),
        // The rental company's booking confirmation (Owner, 13 Oct 2026).
        reservationNumber: Yup.string()
          .trim()
          .max(RENTAL_NUMBER_MAX)
          .required('Reservation number is a required field'),
      }),
  }),
  categoryAllocations: Yup.array().test(
    'category-split',
    'Enter an amount for every category; category amounts must total the expense amount',
    function (rows) {
      const { amount, categories } = this.parent;
      if (categories?.length <= 1) {
        return true;
      }
      if (!rows || rows.length !== categories.length) {
        return false;
      }
      const ids = rows.map((row) => Number(row.categoryId));
      const total = Math.round(Number(amount) * 100);
      return (
        new Set(ids).size === categories.length &&
        categories.every((id: number) => ids.includes(Number(id))) &&
        rows.every(
          (row) =>
            /^-?\d+(\.\d{1,2})?$/.test(String(row.amount)) &&
            Math.sign(Number(row.amount)) === Math.sign(total)
        ) &&
        rows.reduce(
          (sum, row) => sum + Math.round(Number(row.amount) * 100),
          0
        ) === total
      );
    }
  ),
  events: Yup.array()
    .of(
      Yup.object().shape({
        eventId: Yup.number().required('Event is a required field'),
        amount: Yup.number().required('Amount is a required field'),
        percent: Yup.number()
          .required('Percent is a required field')
          .min(0)
          .test(
            'percent-amount',
            'Please fill out the amount first',
            function () {
              return this?.options?.context?.amount;
            }
          ),
      })
    )
    .test(
      'total-percentage',
      'The total percentage must be 100%',
      function (events) {
        const totalPercentage = events?.reduce(
          (sum, event) => sum + (+event.percent || 0),
          0
        );

        return Math.round((totalPercentage || 0) * 100) === 10000;
      }
    )
    .test(
      'total-amount',
      'Event amounts must total the expense amount',
      function (events) {
        const expenseAmount = Number(this.parent.amount);
        if (
          !Number.isFinite(expenseAmount) ||
          !events?.length ||
          events.some(
            (event) =>
              event.amount == null || !Number.isFinite(Number(event.amount))
          )
        ) {
          return true;
        }
        const allocationCents = events.reduce(
          (sum, event) => sum + Math.round(Number(event.amount) * 100),
          0
        );
        return allocationCents === Math.round(expenseAmount * 100);
      }
    ),
});

export const getFormattedDates = (values: FormValues) => ({
  paidDate: values.paidDate ? formatDate(values.paidDate, 'YYYY-MM-DD') : '',
  paymentDate: values.paymentDate
    ? formatDate(values.paymentDate, 'YYYY-MM-DD')
    : '',
});

// The estimate the request carries for a car rental (see
// CarRentalAmountState): the typed amount while it is an estimate, the saved
// estimate once final, nothing for an older rental that never had one.
const getEstimatedAmountCents = (
  values: FormValues,
  saved?: AccountingExpense
) => {
  const state = getCarRentalAmountState(saved);
  if (state === 'final') {
    return Number(saved?.rentalDetail?.estimatedAmountCents);
  }
  return state === 'estimate' ? formRentalCents(values) : undefined;
};

// Builds the `rentalDetail` key of the create/edit request, matching the
// server's three states (main/src/services/accounting.js):
//   - Car Rental selected -> the object (dollars -> integer cents; no
//     estimate is sent as undefined, never 0).
//   - Car Rental removed from an expense that had saved detail (edit) ->
//     explicit null, the only signal that deletes it.
//   - otherwise -> the key is omitted, which leaves saved detail untouched.
// `saved` is the expense being edited (none on create). Callers must drop
// the form-shaped `values.rentalDetail` from their own spread before
// applying this.
export const getRentalDetailPayload = (
  values: FormValues,
  saved?: AccountingExpense
) => {
  if (!isCarRentalSelected(values.categories)) {
    return saved?.rentalDetail ? { rentalDetail: null } : {};
  }
  const detail = values.rentalDetail;
  return {
    rentalDetail: {
      notes: detail.notes || undefined,
      renterName: detail.renterName.trim(),
      agreementNumber: detail.agreementNumber.trim() || undefined,
      estimatedAmountCents: getEstimatedAmountCents(values, saved),
      reservationNumber: detail.reservationNumber.trim() || undefined,
      rentalCompany:
        detail.rentalCompany as AccountingExpenseRentalDetail['rentalCompany'],
      bookingDate: detail.bookingDate
        ? formatDate(detail.bookingDate, 'YYYY-MM-DD')
        : undefined,
    },
  };
};

// The cent-exact split helpers live in utils/event-allocation.ts, shared with
// the stock transfer event split (T-L09); re-exported here unchanged.
export {
  calculateEventAmount,
  calculateEventAmounts,
  calculateEventPercents,
} from 'utils/event-allocation';
